import { type ComponentProps, type FC, type ReactNode } from 'react';
import serverVars from 'server-vars';
import { WithWalletConnectionEnsured } from 'dibs-crypto-utils/exports/WithWalletConnectionEnsured';
import { Button } from 'dibs-elements/exports/Button';
import { ModalBody } from 'dibs-elements/exports/ModalBody';
import { ModalCloseButton } from 'dibs-elements/exports/ModalCloseButton';
import { ModalContainer } from 'dibs-elements/exports/ModalContainer';
import ExclamationCircle from 'dibs-icons/exports/legacy/ExclamationCircle';
import { isRecordWith } from 'dibs-ts-utils/exports/isRecord';

import { HeaderWithImage } from './HeaderWithImage';

import styles from './ActionModal.scss';
import { type ModalStatus } from './types';

const isMobile = serverVars.get('settings.isMobile');

const getHeaderStatus = (arg: ModalStatus): null | 'loading' | 'success' =>
    arg === null
        ? null
        : (
              {
                  loading: 'loading',
                  pinning: 'loading',
                  minting: 'loading',
                  inflight: 'loading',
                  inProgress: 'loading',
                  success: 'success',
                  error: null,
              } as const
          )[arg];

type WalletConnectionProps = ComponentProps<typeof WithWalletConnectionEnsured>;

export type ActionModalProps = {
    children: ReactNode;
    cta?: ReactNode;
    ctaHref?: string;
    ctaOnClick?: () => void;
    ctaDisabled?: boolean;
    secondaryCta?: ReactNode;
    secondaryCtaHref?: string;
    secondaryCtaOnClick?: () => void;
    secondaryCtaDisabled?: boolean;
    nftItemDraft?: ComponentProps<typeof HeaderWithImage>['nftItemDraft'];
    nftItem?: ComponentProps<typeof HeaderWithImage>['nftItem'];
    onClose: () => void;
    status: ModalStatus;
    title?: ReactNode;
    footer?: ReactNode;
    error?: ReactNode | Error;
    viewer?: WalletConnectionProps['viewer'];
};

const MaybeWallet: FC<Partial<WalletConnectionProps>> = ({ viewer, children }) =>
    viewer ? (
        <WithWalletConnectionEnsured viewer={viewer} isMobile={isMobile}>
            {children}
        </WithWalletConnectionEnsured>
    ) : (
        <>{children}</>
    );

const ActionModal: FC<ActionModalProps> = ({
    cta,
    ctaHref,
    ctaOnClick,
    ctaDisabled,
    secondaryCta,
    secondaryCtaHref,
    secondaryCtaOnClick,
    secondaryCtaDisabled,
    nftItemDraft,
    nftItem,
    onClose,
    status,
    title,
    footer,
    viewer,
    error,
    children,
}) => {
    const errorMessage = isRecordWith(error, 'message') ? String(error.message) : error;
    return (
        <ModalContainer isOpen onClose={onClose}>
            <ModalCloseButton onClick={onClose} />
            <MaybeWallet viewer={viewer}>
                <HeaderWithImage
                    nftItemDraft={nftItemDraft}
                    nftItem={nftItem}
                    status={getHeaderStatus(status)}
                    title={title}
                />
                <ModalBody>
                    <div className={styles.body}>{children}</div>
                    {errorMessage && (
                        <div className={styles.error}>
                            <ExclamationCircle className={styles.errorIcon} />
                            {errorMessage && <p className={styles.errorBody}>{errorMessage}</p>}
                        </div>
                    )}
                    {cta && (ctaHref || ctaOnClick) && (
                        <Button
                            fullWidth
                            disabled={ctaDisabled}
                            onClick={ctaOnClick}
                            href={ctaHref}
                        >
                            {cta}
                        </Button>
                    )}
                    {secondaryCta && (secondaryCtaHref || secondaryCtaOnClick) && (
                        <Button
                            fullWidth
                            disabled={secondaryCtaDisabled}
                            onClick={secondaryCtaOnClick}
                            href={secondaryCtaHref}
                            type="secondary"
                            className={styles.secondaryCta}
                        >
                            {secondaryCta}
                        </Button>
                    )}
                    {footer && <p className={styles.footer}>{footer}</p>}
                </ModalBody>
            </MaybeWallet>
        </ModalContainer>
    );
};

export default ActionModal;
